//@ts-strict-ignore
import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import PurchaseOrderValidate from '../../graphql/mutations/purchases/PurchaseOrderValidate.graphql';
import setIn from '../connectedForm/utils/setIn';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import ModalContext from '../Context/ModalContext';
import { alternateFullScreenModalStyle } from './Styles';
import PurchaseOrderForm from '../../views/purchases/PurchaseOrderForm';
import {
  IPurchaseOrder,
  IPurchaseOrderValidateMutationResult,
  IPurchaseOrderValidateMutationVariables,
  ISalesOrderLine,
} from '../../graphql/schema/p4types';
import FluentModal from './FluentModal';

//Hook to contain functionality for raising a PO from a Sales Order Line.
//Extracted as needs to be available both from Alt Product Modal (Initial Raise of PO) & Line Details Panel (Editing it in case of errors)..
export const usePurchaseOrderCapability = ({
  onChange,
  onClose,
  value,
}: {
  onChange: (newValue: ISalesOrderLine) => void;
  onClose: () => void;
  value?: ISalesOrderLine;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const [savedPurchaseOrder, setSavedPurchaseOrder] = useState(null);
  const [purchaseOrderState, setPurchaseOrderState] = useState<{
    modalOpen: boolean;
    initialValues: {} | IPurchaseOrder;
  }>({ modalOpen: false, initialValues: {} });

  const onError = useComponentQueryErrorHandling();
  const [validatePurchaseOrder] = useMutation<
    IPurchaseOrderValidateMutationResult,
    IPurchaseOrderValidateMutationVariables
  >(PurchaseOrderValidate, {
    onCompleted: () => {
      let lineValue = value!;
      lineValue = setIn(lineValue, 'generatedPurchaseOrder', savedPurchaseOrder);
      onChange(lineValue);

      connectedForm.setSupportingData?.((existing: any) => ({
        ...existing,
        salesLineNewPurchaseOrderMap: {
          ...(existing.salesLineNewPurchaseOrderMap || {}),
          [lineValue.uuid!]: savedPurchaseOrder,
        },
      }));

      setPurchaseOrderState({
        modalOpen: false,
        initialValues: {},
      });
      onClose(); //No need to continue displaying alt product modal once we've raised the order.
    },
    onError: onError,
  });

  useEffect(() => {
    if (savedPurchaseOrder) {
      validatePurchaseOrder({
        variables: {
          input: { clientMutationId: getClientMutationId(), purchaseOrder: normalizeVariables(savedPurchaseOrder) },
        },
      });
    }
  }, [savedPurchaseOrder, validatePurchaseOrder]);

  const onPurchaseOrderModalClose = (purchaseOrder) => {
    if (purchaseOrder?.hasOwnProperty('id')) {
      setSavedPurchaseOrder(purchaseOrder);
      return;
    }
    setPurchaseOrderState({
      modalOpen: false,
      initialValues: {},
    });
    onClose(); //No need to continue displaying alt product modal once we've raised the order.
  };

  const AnyPurchaseOrderForm = PurchaseOrderForm as any;

  return {
    purchaseOrderState,
    setPurchaseOrderState,
    modals: (
      <ModalContext.Provider value={{ onClose: onPurchaseOrderModalClose, style: alternateFullScreenModalStyle }}>
        <FluentModal
          isOpen={purchaseOrderState.modalOpen}
          onDismiss={onPurchaseOrderModalClose}
          styles={{
            main: {
              width: '80vw',
              height: '90vh',
              overflowY: 'unset',
            },
            scrollableContent: {
              height: '100%',
            },
          }}
        >
          <AnyPurchaseOrderForm
            modalClose={true}
            formData={{ defaultRecord: purchaseOrderState.initialValues }}
            onClose={onPurchaseOrderModalClose}
            saveViaWrappingComponent={true}
            saveOptions={[
              {
                value: 'EXIT',
                label: 'Save Draft & Exit',
              },
            ]}
          />
        </FluentModal>
      </ModalContext.Provider>
    ),
  };
};
